import { useEffect, useMemo, useRef, useState } from "react";
import { Eye, Loader2 } from "lucide-react";
import {
  bulkTimetableImportApi,
  type CatalogTarget,
  type ShopItem,
} from "@/services/api/bulkTimetableImport";
import { timetableImportV2Api } from "@/services/api/timetableImportV2";
import { listBackgrounds } from "@/services/api/qualification";
import type { Background } from "@/types/qualification";
import { ProgressDialog } from "@/components/common/ProgressDialog";
import { PdfPanel } from "./PdfPanel";
import { MultiSplitDialog } from "./MultiSplitDialog";
import { SplitDialog } from "./SplitDialog";
import { SplitPartAssignPanel } from "./SplitPartAssignPanel";
import {
  isIgnoredToken,
  knownIgnoredTokenNames,
  loadIgnoredTokenNames,
  rememberIgnoredToken,
  rememberIgnoredTokens,
} from "@/utils/ignoredTokens";
import { loadSplitHints } from "@/utils/loadSplitHints";
import { hintsForShop, planShopAutoSplit } from "@/utils/shopAutoSplit";
import {
  blockedSplitField,
  fieldFromBlockMessage,
  occupiedFieldNote,
  splitLands,
} from "@/utils/shopSplitLanding";
import {
  characterTones,
  EMPTY_SPLIT_HINTS,
  type SplitHints,
} from "@/utils/splitSuggestion";
import {
  canSplitToken,
  mergeSplitQueue,
  shopItemKey,
  shopRowsForDialogToken,
} from "@/utils/tokenSplit";
import type { SplitPartGroup } from "@/utils/splitPartGroups";
import { compactKey } from "@/utils/tokenPatterns";

const FIELD_LABELS: Record<string, string> = {
  subject: "Fach",
  room: "Raum",
  class_name: "Klasse",
  ignore: "Ignorieren",
};

const SHOP_FIELDS = ["subject", "room", "class_name"] as const;
const PART_FIELDS = [...SHOP_FIELDS, "ignore"] as const;

const CREATE_LABELS: Record<string, string> = {
  subject: "Neues Fach",
  room: "Neuer Raum",
  class_name: "Neue Klasse",
};

const BATCH_CREATE_NOTE: Record<string, string> = {
  subject: "Jeder Haken wird ein eigenes Fach. Der Name ist das Token.",
  room: "Jeder Haken wird ein eigener Raum. Der Name ist das Token.",
  class_name: "Jeder Haken wird eine eigene Klasse. Der Name ist das Token.",
};

const BATCH_CREATE_BUTTON: Record<string, (count: number) => string> = {
  subject: (count) => `${count} Fächer anlegen`,
  room: (count) => `${count} Räume anlegen`,
  class_name: (count) => `${count} Klassen anlegen`,
};

const BATCH_PROGRESS_HINT = "Die Auswahl wird der Reihe nach gespeichert.";

function batchProgressTitle(action?: string): string {
  if (action === "create") return "Einträge werden angelegt…";
  if (action === "ignore") return "Tokens werden ignoriert…";
  if (action === "alias") return "Tokens werden zugeordnet…";
  if (action === "split") return "Tokens werden geteilt…";
  return "Batch wird verarbeitet…";
}

function asShopItem(item: ShopItem): ShopItem {
  return {
    token: item.token,
    field: item.field,
    display: item.display || item.token,
    pages: item.pages || [],
    suggested_id: item.suggested_id || null,
    suggested_ids: item.suggested_ids || [],
    occupied: item.occupied,
  };
}

function emptyTargets(): Record<string, CatalogTarget[]> {
  return { subject: [], room: [], class_name: [] };
}

const ROOM_LIKE = /^(bsd|raum|room|saal)([-.]|$)/i;
const CLASS_LIKE = /[A-Za-z]{2,}\d{2,}|\d+[A-Za-z]/;

function catalogNames(
  targets: Record<string, CatalogTarget[]>,
  field: string
): string[] {
  return (targets[field] || [])
    .flatMap((row) => [row.name, row.code, ...(row.aliases || [])])
    .filter(Boolean)
    .map((v) => String(v).toLowerCase());
}

function catalogHas(
  targets: Record<string, CatalogTarget[]>,
  field: string,
  part: string
): boolean {
  const needle = part.trim().toLowerCase();
  if (!needle) return false;
  return catalogNames(targets, field).includes(needle);
}

function uniqueCatalogHit(
  targets: Record<string, CatalogTarget[]>,
  part: string
): { field: string; id: string; name: string } | null {
  const needle = part.trim().toLowerCase();
  const compact = compactKey(part);
  if (!needle) return null;
  const hits: { field: string; id: string; name: string }[] = [];
  for (const field of SHOP_FIELDS) {
    for (const row of targets[field] || []) {
      const values = [row.name, row.code, ...(row.aliases || [])]
        .filter(Boolean)
        .map((value) => String(value));
      const exact = values.some((value) => {
        const text = value.trim().toLowerCase();
        return (
          text === needle ||
          (compact.length > 0 && compactKey(value) === compact)
        );
      });
      if (exact) hits.push({ field, id: row.id, name: row.name });
    }
  }
  const keys = new Set(hits.map((hit) => `${hit.field}:${hit.id}`));
  if (keys.size !== 1) return null;
  return hits[0] ?? null;
}

function catalogPrefix(
  targets: Record<string, CatalogTarget[]>,
  field: string,
  part: string
): boolean {
  const needle = part.trim().toLowerCase();
  if (needle.length < 4) return false;
  return catalogNames(targets, field).some(
    (name) => name === needle || name.startsWith(needle)
  );
}

function guessPartFields(
  parts: string[],
  sourceField: string,
  targets: Record<string, CatalogTarget[]>,
  ignored: string[] = []
): string[] {
  const used = new Set<string>();
  return parts.map((part) => {
    if (isIgnoredToken(part, ignored)) return "ignore";
    const known = uniqueCatalogHit(targets, part);
    if (known && !used.has(known.field)) {
      used.add(known.field);
      return known.field;
    }
    let field: string | null = null;
    if (catalogHas(targets, "subject", part)) field = "subject";
    else if (
      catalogHas(targets, "room", part) ||
      catalogPrefix(targets, "room", part) ||
      ROOM_LIKE.test(part)
    )
      field = "room";
    else if (
      catalogHas(targets, "class_name", part) ||
      catalogPrefix(targets, "class_name", part) ||
      CLASS_LIKE.test(part)
    )
      field = "class_name";
    if (!field || used.has(field)) {
      field =
        [sourceField, "class_name", "room", "subject"].find(
          (candidate) => candidate && !used.has(candidate)
        ) || sourceField;
    }
    used.add(field);
    return field;
  });
}

function matchesTarget(target: CatalogTarget, query: string): boolean {
  if (!query) return true;
  const hay = [target.name, target.code, ...(target.aliases || [])]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(query);
}

function knownSplitField(
  part: string,
  hints: SplitHints,
  ignored: string[]
): string {
  if (isIgnoredToken(part, ignored)) return "ignore";
  const tone = characterTones(part, [], hints).find((item) => item) ?? null;
  if (tone === "klass") return "class_name";
  if (tone === "room" || tone === "subject" || tone === "ignore") return tone;
  return "";
}

function readySplitFields(
  parts: string[],
  hints: SplitHints,
  ignored: string[]
): string[] | null {
  const fields = parts.map((part) => knownSplitField(part, hints, ignored));
  if (fields.some((field) => !field)) return null;
  const placed = fields.filter((field) => field !== "ignore");
  if (new Set(placed).size !== placed.length) return null;
  return fields;
}

type PendingShopSplit = { item: ShopItem; parts: string[] };

function splitPartId(item: ShopItem, index: number): string {
  return `${item.token}|${item.field}:${index}`;
}

function shopThemeLabel(
  item: ShopItem,
  catalog: Record<string, CatalogTarget[]>
): string {
  if ((item.suggested_ids || []).length > 1 && !item.suggested_id)
    return "mehrdeutig";
  if (item.suggested_id) {
    const row = (catalog[item.field] || []).find(
      (target) => target.id === item.suggested_id
    );
    if (row?.name) return row.name;
  }
  const hit = uniqueCatalogHit(catalog, item.display || item.token);
  if (hit && hit.field === item.field) return hit.name;
  return "ohne Ziel";
}

function themeRank(label: string): number {
  if (label === "mehrdeutig") return 1;
  if (label === "ohne Ziel") return 2;
  return 0;
}

type ShopThemeGroup = {
  field: string;
  label: string;
  themes: { label: string; items: ShopItem[] }[];
};

function groupShopItems(
  rows: ShopItem[],
  catalog: Record<string, CatalogTarget[]>
): ShopThemeGroup[] {
  const fieldOrder = SHOP_FIELDS as readonly string[];
  const sorted = [...rows].sort((a, b) => {
    const fa = fieldOrder.indexOf(a.field);
    const fb = fieldOrder.indexOf(b.field);
    const fieldCmp = (fa < 0 ? 99 : fa) - (fb < 0 ? 99 : fb);
    if (fieldCmp) return fieldCmp;
    const ta = shopThemeLabel(a, catalog);
    const tb = shopThemeLabel(b, catalog);
    const rankCmp = themeRank(ta) - themeRank(tb);
    if (rankCmp) return rankCmp;
    if (ta !== tb) return ta.localeCompare(tb, "de");
    return (a.display || a.token).localeCompare(b.display || b.token, "de");
  });
  const groups: ShopThemeGroup[] = [];
  for (const item of sorted) {
    let group = groups[groups.length - 1];
    if (!group || group.field !== item.field) {
      group = {
        field: item.field,
        label: FIELD_LABELS[item.field] || item.field,
        themes: [],
      };
      groups.push(group);
    }
    const label = shopThemeLabel(item, catalog);
    let theme = group.themes[group.themes.length - 1];
    if (!theme || theme.label !== label) {
      theme = { label, items: [] };
      group.themes.push(theme);
    }
    theme.items.push(item);
  }
  return groups;
}

function visibleShopItems(
  rows: ShopItem[],
  ignored: string[] = []
): ShopItem[] {
  return rows.filter(
    (item) =>
      item.field !== "teacher_code" && !isIgnoredToken(item.token, ignored)
  );
}

function orderedShopItems(
  rows: ShopItem[],
  catalog: Record<string, CatalogTarget[]>
): ShopItem[] {
  return groupShopItems(rows, catalog).flatMap((group) =>
    group.themes.flatMap((theme) => theme.items)
  );
}

function indexOfShopItem(
  rows: ShopItem[],
  token: string,
  field: string
): number {
  const index = rows.findIndex(
    (item) => item.token === token && item.field === field
  );
  return index >= 0 ? index : 0;
}

function firstThematicIndex(
  rows: ShopItem[],
  catalog: Record<string, CatalogTarget[]>
): number {
  const first = orderedShopItems(rows, catalog)[0];
  if (!first) return 0;
  return indexOfShopItem(rows, first.token, first.field);
}

function nextThematicIndex(
  before: ShopItem[],
  after: ShopItem[],
  catalog: Record<string, CatalogTarget[]>,
  resolved: { token: string; field: string }
): number {
  const ordered = orderedShopItems(before, catalog);
  const pos = ordered.findIndex(
    (item) => item.token === resolved.token && item.field === resolved.field
  );
  const start = pos >= 0 ? pos + 1 : 0;
  const still = (item: ShopItem) =>
    after.some((row) => row.token === item.token && row.field === item.field);
  const next =
    ordered.slice(start).find(still) ??
    [...ordered.slice(0, Math.max(pos, 0))].reverse().find(still);
  if (!next) return 0;
  return indexOfShopItem(after, next.token, next.field);
}

function isAbortError(error: unknown): boolean {
  const name = (error as { name?: string; code?: string })?.name;
  const code = (error as { code?: string })?.code;
  return (
    name === "AbortError" || name === "CanceledError" || code === "ERR_CANCELED"
  );
}

function withSignal<T>(work: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted)
    return Promise.reject(new DOMException("Aborted", "AbortError"));
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(new DOMException("Aborted", "AbortError"));
    signal.addEventListener("abort", onAbort, { once: true });
    work.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", onAbort);
        reject(error);
      }
    );
  });
}

function shopError(error: unknown, fallback: string): string {
  const ax = error as {
    response?: { data?: { detail?: string } };
    message?: string;
  };
  return ax.response?.data?.detail || ax.message || fallback;
}

async function aliasKnownShopItems(
  batchId: string,
  remaining: ShopItem[],
  catalog: Record<string, CatalogTarget[]>
): Promise<{
  remaining: ShopItem[];
  catalog: Record<string, CatalogTarget[]>;
  canUndo: boolean;
  ignored: string[];
}> {
  const seen = new Set<string>();
  let rows = remaining;
  let targets = catalog;
  let canUndo = false;
  let ignored: string[] = [];
  while (rows.length > 0) {
    const next = rows.find((item) => {
      const key = `${item.token}|${item.field}`;
      const hit = uniqueCatalogHit(targets, item.token);
      return !seen.has(key) && !!hit && hit.field === item.field;
    });
    const hit = next ? uniqueCatalogHit(targets, next.token) : null;
    if (!next || !hit || hit.field !== next.field) break;
    seen.add(`${next.token}|${next.field}`);
    const result = await bulkTimetableImportApi.resolveShopItem(batchId, {
      token: next.token,
      field: next.field,
      source_field: next.field,
      canonical_id: hit.id,
      action: "alias",
    });
    rows = result.remaining || [];
    if (result.targets) targets = { ...emptyTargets(), ...result.targets };
    canUndo = !!result.can_undo;
    if (result.ignored_tokens?.length) ignored = result.ignored_tokens;
  }
  return { remaining: rows, catalog: targets, canUndo, ignored };
}

export function BulkShopDialog({
  batchId,
  onDone,
  onCancel,
  onEmpty,
}: {
  batchId: string;
  onDone: () => void;
  onCancel: () => void;
  onEmpty?: () => void;
}) {
  const [items, setItems] = useState<ShopItem[]>([]);
  const [fileId, setFileId] = useState<string | null>(null);
  const [targets, setTargets] =
    useState<Record<string, CatalogTarget[]>>(emptyTargets);
  const [canonicalId, setCanonicalId] = useState("");
  const [newName, setNewName] = useState("");
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState<"alias" | "create">("alias");
  const [busy, setBusy] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{
    title: string;
    current: number;
    total: number;
    status?: string;
  } | null>(null);
  const batchAbort = useRef<AbortController | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(0);
  const [fieldOverride, setFieldOverride] = useState<string | null>(null);
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitDraft, setSplitDraft] = useState<{
    parts: string[];
    fields: string[];
  } | null>(null);
  const [splitHints, setSplitHints] = useState<SplitHints>(EMPTY_SPLIT_HINTS);
  const [multiSplitRows, setMultiSplitRows] = useState<ShopItem[]>([]);
  const [pendingShopSplits, setPendingShopSplits] = useState<
    PendingShopSplit[] | null
  >(null);
  const [shopSplitGroups, setShopSplitGroups] = useState<
    SplitPartGroup[] | null
  >(null);
  const queuedShopSplits = useRef<PendingShopSplit[]>([]);
  const heldSplitPayloads = useRef<
    Array<{ item: ShopItem; parts: { token: string; field: string }[] }>
  >([]);
  const splitFlight = useRef(false);
  const splitQueue = useRef<
    Array<{ item: ShopItem; parts: { token: string; field: string }[] }>
  >([]);
  const assignOpen = useRef(false);
  const assignRows = useRef<PendingShopSplit[]>([]);
  const [ignoredNames, setIgnoredNames] = useState<string[]>([]);
  const [itemIndex, setItemIndex] = useState(0);
  const [listMode, setListMode] = useState<"single" | "multi">("single");
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [canUndo, setCanUndo] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [backgrounds, setBackgrounds] = useState<Background[]>([]);
  const [backgroundIds, setBackgroundIds] = useState<Set<string>>(new Set());
  const [pageTeachers, setPageTeachers] = useState<Record<number, string>>({});
  const [totalPages, setTotalPages] = useState(0);
  const [resolvedPages, setResolvedPages] = useState(0);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const onEmptyRef = useRef(onEmpty);
  onEmptyRef.current = onEmpty;

  const applyRemaining = (
    remaining: ShopItem[],
    nextTargets?: Record<string, CatalogTarget[]>,
    importIfEmpty = true,
    nextIndex = 0,
    nextCanUndo = false,
    ignored: string[] = ignoredNames
  ) => {
    const prev = new Map(
      items.map((item) => [`${item.token}|${item.field}`, item])
    );
    const next = visibleShopItems(remaining, ignored).map((item) => {
      const row = asShopItem(item);
      const old = prev.get(`${row.token}|${row.field}`);
      if (!old) return row;
      return {
        ...row,
        pages: row.pages?.length ? row.pages : old.pages || [],
        suggested_id: row.suggested_ids?.length
          ? row.suggested_id || null
          : row.suggested_id || old.suggested_id || null,
        suggested_ids: row.suggested_ids?.length
          ? row.suggested_ids
          : old.suggested_ids || [],
        occupied: row.occupied || old.occupied,
      };
    });
    setItems(next);
    setCanUndo(nextCanUndo);
    setItemIndex(
      Math.max(0, Math.min(nextIndex, Math.max(next.length - 1, 0)))
    );
    if (nextTargets) setTargets({ ...emptyTargets(), ...nextTargets });
    if (!next.length) {
      if (importIfEmpty) {
        setDismissed(true);
        onDoneRef.current();
      } else if (onEmptyRef.current) {
        setDismissed(true);
        onEmptyRef.current();
      }
    }
  };

  useEffect(() => {
    listBackgrounds()
      .then(setBackgrounds)
      .catch(() => setBackgrounds([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    timetableImportV2Api
      .getBatchStatus(batchId)
      .then((status) => {
        if (cancelled) return;
        const map: Record<number, string> = {};
        for (const section of status.sections || []) {
          const code = section.section_identifier?.trim();
          if (!code) continue;
          const listed = section.section_pages?.length
            ? section.section_pages
            : [section.section_index + 1];
          for (const page of listed) {
            if (page > 0) map[page] = code;
          }
        }
        setPageTeachers(map);
        setTotalPages(status.total_sections || 0);
        setResolvedPages(status.resolved_sections || 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [batchId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const data = await bulkTimetableImportApi.getShopItems(batchId);
        if (cancelled) return;
        setFileId(data.file_id || null);
        const loadedIgnored = await loadIgnoredTokenNames().catch(
          () => data.ignored_tokens || []
        );
        const ignored = [
          ...new Set([...(data.ignored_tokens || []), ...loadedIgnored]),
        ];
        if (ignored.length) rememberIgnoredTokens(ignored);
        setIgnoredNames(ignored);
        let catalog = { ...emptyTargets(), ...(data.targets || {}) };
        setTargets(catalog);
        const baseHints = await loadSplitHints().catch(() => EMPTY_SPLIT_HINTS);
        const hints = hintsForShop(baseHints, catalog, ignored);
        setSplitHints(hints);
        let remaining = data.items || [];
        let canUndo = !!data.can_undo;
        const keptWhole = new Set(
          (data.kept_whole || [])
            .map((token) => token.trim().toLowerCase())
            .filter(Boolean)
        );
        const opened = visibleShopItems(remaining, ignored);
        applyRemaining(
          remaining,
          catalog,
          false,
          firstThematicIndex(opened, catalog),
          canUndo,
          ignored
        );
        if (!cancelled) setLoading(false);
        const tried = new Set<string>();
        const cap = Math.max(remaining.length * 4, 8);
        for (let step = 0; step < cap; step += 1) {
          const next = visibleShopItems(remaining, ignored).find((item) => {
            const key = `${item.token}|${item.field}`;
            return (
              !keptWhole.has(item.token.trim().toLowerCase()) &&
              !tried.has(key) &&
              planShopAutoSplit(
                item.display || item.token,
                item.field,
                hints,
                ignored
              )
            );
          });
          if (!next) break;
          const key = `${next.token}|${next.field}`;
          tried.add(key);
          const parts = planShopAutoSplit(
            next.display || next.token,
            next.field,
            hints,
            ignored
          );
          if (
            !parts ||
            !splitLands(next.occupied, next.field, parts, next.token)
          )
            continue;
          try {
            const result = await bulkTimetableImportApi.resolveShopItem(
              batchId,
              {
                token: next.token,
                field: next.field,
                source_field: next.field,
                action: "split",
                parts,
              }
            );
            if (cancelled) return;
            remaining = result.remaining || [];
            canUndo = !!result.can_undo;
            if (result.targets)
              catalog = { ...emptyTargets(), ...result.targets };
            if (result.ignored_tokens?.length) {
              for (const name of result.ignored_tokens) {
                if (!isIgnoredToken(name, ignored)) ignored.push(name);
              }
              rememberIgnoredTokens(result.ignored_tokens);
            }
            if (
              remaining.some(
                (item) => item.token === next.token && item.field === next.field
              )
            )
              continue;
            const ignoredParts = parts
              .filter((part) => part.field === "ignore")
              .map((part) => part.token);
            rememberIgnoredTokens(ignoredParts);
            for (const part of ignoredParts) {
              if (!isIgnoredToken(part, ignored)) ignored.push(part);
            }
            const aliased = await aliasKnownShopItems(
              batchId,
              remaining,
              catalog
            );
            if (cancelled) return;
            remaining = aliased.remaining;
            catalog = aliased.catalog;
            if (aliased.canUndo) canUndo = true;
            if (aliased.ignored.length) {
              for (const name of aliased.ignored) {
                if (!isIgnoredToken(name, ignored)) ignored.push(name);
              }
              rememberIgnoredTokens(aliased.ignored);
            }
          } catch {
            continue;
          }
        }
        if (cancelled) return;
        setSplitHints(hintsForShop(baseHints, catalog, ignored));
        setIgnoredNames(ignored);
        setTargets(catalog);
        const visible = visibleShopItems(remaining, ignored);
        applyRemaining(
          remaining,
          catalog,
          false,
          firstThematicIndex(visible, catalog),
          canUndo,
          ignored
        );
      } catch (e) {
        if (!cancelled) setError(shopError(e, "Shop laden fehlgeschlagen"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  const current = items[Math.min(itemIndex, Math.max(items.length - 1, 0))];
  const pages = current?.pages || [];
  const selectedField = fieldOverride || current?.field || "";
  const options = useMemo(
    () => (selectedField ? targets[selectedField] || [] : []),
    [targets, selectedField]
  );

  useEffect(() => {
    setFieldOverride(null);
    setSplitOpen(false);
    setSplitDraft(null);
  }, [current?.token, current?.field]);

  useEffect(() => {
    setPreviewIdx(0);
    if (!pages.length) setPreviewOpen(false);
  }, [current?.token, current?.field, pages.length]);

  useEffect(() => {
    const display = current?.display || "";
    setNewName(display);
    setSearch("");
    setError(null);
    const needle = display.trim().toLowerCase();
    const compact = compactKey(display);
    const suggested = current?.suggested_id
      ? options.find((s) => s.id === current.suggested_id)
      : undefined;
    const collision =
      !current?.suggested_id && (current?.suggested_ids || []).length > 1;
    const match =
      suggested ||
      (!collision && needle
        ? options.find((s) => (s.name || "").trim().toLowerCase() === needle) ||
          options.find((s) => (s.code || "").trim().toLowerCase() === needle) ||
          options.find((s) =>
            (s.aliases || []).some(
              (alias) => alias.trim().toLowerCase() === needle
            )
          ) ||
          (compact
            ? options.find(
                (s) =>
                  compactKey(s.name || "") === compact ||
                  compactKey(s.code || "") === compact ||
                  (s.aliases || []).some(
                    (alias) => compactKey(alias) === compact
                  )
              )
            : undefined)
        : undefined);
    setCanonicalId(match?.id || "");
    setBackgroundIds(new Set());
    setMode(options.length ? "alias" : "create");
  }, [
    current?.token,
    selectedField,
    current?.display,
    current?.suggested_id,
    current?.suggested_ids,
    options,
  ]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return options.filter((target) => matchesTarget(target, query));
  }, [options, search]);

  const shopGroups = useMemo(
    () => groupShopItems(items, targets),
    [items, targets]
  );
  const orderedItems = useMemo(
    () =>
      shopGroups.flatMap((group) =>
        group.themes.flatMap((theme) => theme.items)
      ),
    [shopGroups]
  );
  const selection = useMemo(
    () =>
      listMode === "multi"
        ? items.filter((item) => selectedKeys.has(shopItemKey(item)))
        : [],
    [items, listMode, selectedKeys]
  );

  useEffect(() => {
    setSelectedKeys((prev) => {
      if (prev.size === 0) return prev;
      const live = new Set(items.map(shopItemKey));
      let changed = false;
      const next = new Set<string>();
      prev.forEach((key) => {
        if (live.has(key)) next.add(key);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [items]);

  const selectedTarget = options.find((target) => target.id === canonicalId);
  const identityMatch = !!(
    current &&
    selectedTarget &&
    [selectedTarget.name, selectedTarget.code]
      .filter(Boolean)
      .some(
        (value) =>
          (value ?? "").trim().toLowerCase() ===
          (current.display || "").trim().toLowerCase()
      )
  );

  const finishResolve = async (payload: {
    token: string;
    field: string;
    source_field?: string;
    canonical_id?: string;
    action?: string;
    name?: string;
    parts?: { token: string; field: string }[];
    background_ids?: string[];
  }) => {
    setBusy(true);
    setError(null);
    try {
      let result = await bulkTimetableImportApi.resolveShopItem(
        batchId,
        payload
      );
      setCanonicalId("");
      let remaining = result.remaining || [];
      if (
        payload.action === "split" &&
        !remaining.some(
          (item) =>
            item.token === payload.token &&
            item.field === (payload.source_field || payload.field)
        )
      ) {
        rememberIgnoredTokens(
          (payload.parts || [])
            .filter((part) => part.field === "ignore")
            .map((part) => part.token)
        );
      }
      if (
        payload.action === "split" &&
        remaining.some((item) => item.token === payload.token)
      ) {
        setError(
          "Split nicht übernommen. Anderes Feld wählen oder Ziel-Feld ist belegt."
        );
      }
      const sourceField = payload.source_field || payload.field;
      const ignored = result.ignored_tokens || [];
      const merged = {
        ...emptyTargets(),
        ...targets,
        ...(result.targets || {}),
      };
      const after = visibleShopItems(remaining, ignored);
      const still = after.find(
        (item) =>
          item.token === payload.token &&
          (item.field === sourceField || item.field === payload.field)
      );
      const nextIndex =
        payload.action !== "split" && still && !result.closed
          ? indexOfShopItem(after, still.token, still.field)
          : nextThematicIndex(items, after, merged, {
              token: payload.token,
              field: sourceField,
            });
      if (ignored.length) {
        rememberIgnoredTokens(ignored);
        setIgnoredNames(ignored);
      }
      applyRemaining(
        remaining,
        result.targets,
        true,
        nextIndex,
        !!result.can_undo,
        ignored
      );
    } catch (e) {
      setError(shopError(e, "Zuordnen fehlgeschlagen"));
    } finally {
      setBusy(false);
    }
  };

  const runBatch = async (
    rows: ShopItem[],
    build: (row: ShopItem) => {
      token: string;
      field: string;
      source_field?: string;
      canonical_id?: string;
      action?: string;
      name?: string;
      background_ids?: string[];
    }
  ) => {
    setBusy(true);
    setError(null);
    const sample = rows[0] ? build(rows[0]) : undefined;
    const title = batchProgressTitle(sample?.action);
    const controller = new AbortController();
    batchAbort.current = controller;
    const mark = (done: number, token?: string) => {
      const current = Math.min(done + (token ? 1 : 0), rows.length);
      setBatchProgress({
        title,
        current,
        total: rows.length,
        status: token
          ? `${current} von ${rows.length} · ${token}`
          : `${done} von ${rows.length} verarbeitet`,
      });
    };
    mark(0, rows[0] ? rows[0].display || rows[0].token : undefined);
    let result: Awaited<
      ReturnType<typeof bulkTimetableImportApi.resolveShopItem>
    > | null = null;
    let last: ShopItem | null = rows[rows.length - 1] || null;
    let failed: string | null = null;
    const done = rows.length;
    try {
      if (!controller.signal.aborted && rows.length > 0) {
        try {
          const head = rows[0];
          if (head) mark(0, head.display || head.token);
          result = await withSignal(
            bulkTimetableImportApi.resolveShopItems(
              batchId,
              rows.map(build),
              controller.signal
            ),
            controller.signal
          );
          mark(done);
        } catch (e) {
          if (!(controller.signal.aborted || isAbortError(e))) {
            failed = shopError(e, "Zuordnen fehlgeschlagen");
          }
        }
      }
      setSelectedKeys(new Set());
      setCanonicalId("");
      if (result && last) {
        const ignored = result.ignored_tokens || [];
        if (ignored.length) {
          rememberIgnoredTokens(ignored);
          setIgnoredNames(ignored);
        }
        const merged = {
          ...emptyTargets(),
          ...targets,
          ...(result.targets || {}),
        };
        const after = visibleShopItems(result.remaining || [], ignored);
        applyRemaining(
          result.remaining || [],
          result.targets,
          true,
          nextThematicIndex(items, after, merged, {
            token: last.token,
            field: last.field,
          }),
          !!result.can_undo,
          ignored
        );
      }
      if (failed) setError(failed);
    } finally {
      if (batchAbort.current === controller) batchAbort.current = null;
      setBatchProgress(null);
      setBusy(false);
    }
  };

  const applySplitBatch = async (
    rows: Array<{ item: ShopItem; parts: { token: string; field: string }[] }>
  ) => {
    if (rows.length === 0) return;
    if (splitFlight.current) {
      splitQueue.current.push(...rows);
      return;
    }
    splitFlight.current = true;
    setBusy(true);
    setError(null);
    const controller = new AbortController();
    batchAbort.current = controller;
    const splitTitle = batchProgressTitle("split");
    const splitTotal = Math.max(rows.length, 1);
    setBatchProgress({
      title: splitTitle,
      current: 1,
      total: splitTotal,
      status: `1 von ${splitTotal} · ${rows[0]?.item.display || rows[0]?.item.token || ""}`,
    });
    try {
      let pending = rows;
      let done = 0;
      while (pending.length) {
        let result: Awaited<
          ReturnType<typeof bulkTimetableImportApi.resolveShopItem>
        > | null = null;
        let last: ShopItem | null = null;
        const blocked: Array<{
          item: ShopItem;
          parts: { token: string; field: string }[];
          message: string;
        }> = [];
        let aliasError: string | null = null;
        let catalog = targets;
        for (const row of pending) {
          if (controller.signal.aborted) break;
          if (
            result &&
            !(result.remaining || []).some(
              (item) =>
                item.token === row.item.token && item.field === row.item.field
            )
          ) {
            continue;
          }
          try {
            const total = Math.max(rows.length, done + pending.length);
            setBatchProgress({
              title: splitTitle,
              current: done + 1,
              total,
              status: `${done + 1} von ${total} · ${row.item.display || row.item.token}`,
            });
            result = await withSignal(
              bulkTimetableImportApi.resolveShopItem(
                batchId,
                {
                  token: row.item.token,
                  field: row.item.field,
                  source_field: row.item.field,
                  action: "split",
                  parts: row.parts,
                },
                controller.signal
              ),
              controller.signal
            );
            done += 1;
            last = row.item;
            const remaining = result.remaining || [];
            if (
              remaining.some(
                (item) =>
                  item.token === row.item.token && item.field === row.item.field
              )
            ) {
              blocked.push({
                item: row.item,
                parts: row.parts,
                message:
                  "Split nicht übernommen. Anderes Feld wählen oder Ziel-Feld ist belegt.",
              });
              continue;
            }
            rememberIgnoredTokens(
              row.parts
                .filter((part) => part.field === "ignore")
                .map((part) => part.token)
            );
            if (result.targets)
              catalog = { ...emptyTargets(), ...result.targets };
          } catch (e) {
            if (controller.signal.aborted || isAbortError(e)) break;
            blocked.push({
              item: row.item,
              parts: row.parts,
              message: shopError(e, "Teilen fehlgeschlagen"),
            });
            continue;
          }
        }
        setSelectedKeys(new Set());
        if (result && last) {
          const ignored = result.ignored_tokens || [];
          if (ignored.length) {
            rememberIgnoredTokens(ignored);
            setIgnoredNames(ignored);
          }
          const merged = {
            ...emptyTargets(),
            ...catalog,
            ...(result.targets || {}),
          };
          const after = visibleShopItems(result.remaining || [], ignored);
          applyRemaining(
            result.remaining || [],
            result.targets || catalog,
            true,
            nextThematicIndex(items, after, merged, {
              token: last.token,
              field: last.field,
            }),
            !!result.can_undo,
            ignored
          );
        }
        if (blocked.length) {
          const messages = [
            ...new Set(
              [aliasError, ...blocked.map((row) => row.message)].filter(
                (item): item is string => Boolean(item)
              )
            ),
          ];
          setError(messages.join(" "));
          const incoming = blocked.map((row) => ({
            item: row.item,
            parts: row.parts.map((part) => part.token),
          }));
          const seen = new Set(
            assignRows.current.map(
              (row) => `${row.item.token}|${row.item.field}`
            )
          );
          const added = incoming.filter(
            (row) => !seen.has(`${row.item.token}|${row.item.field}`)
          );
          assignRows.current = [...assignRows.current, ...added];
          assignOpen.current = true;
          setPendingShopSplits(assignRows.current);
          setShopSplitGroups((prev) => {
            const have = new Set((prev || []).map((group) => group.parent));
            const nextGroups = blocked
              .filter((row) => !have.has(row.item.display || row.item.token))
              .map((row) => ({
                parent: row.item.display || row.item.token,
                parts: row.parts.map((part, index) => {
                  const named = fieldFromBlockMessage(row.message);
                  const taken =
                    (named === part.field ? named : null) ||
                    blockedSplitField(
                      row.item.occupied,
                      row.item.field,
                      part,
                      row.item.token
                    );
                  return {
                    token: part.token,
                    id: splitPartId(row.item, index),
                    nested: false,
                    assignable: true,
                    field: taken ? null : part.field,
                    hint: taken
                      ? occupiedFieldNote(taken, row.item.occupied?.[taken])
                      : undefined,
                  };
                }),
              }));
            return [...(prev || []), ...nextGroups];
          });
        } else if (aliasError) {
          setError(aliasError);
        }
        pending = controller.signal.aborted ? [] : splitQueue.current.splice(0);
      }
    } finally {
      splitFlight.current = false;
      if (batchAbort.current === controller) batchAbort.current = null;
      setBatchProgress(null);
      setBusy(false);
    }
  };

  const closeMultiSplit = () => {
    assignOpen.current = false;
    assignRows.current = [];
    queuedShopSplits.current = [];
    heldSplitPayloads.current = [];
    setMultiSplitRows([]);
    setPendingShopSplits(null);
    setShopSplitGroups(null);
  };

  const acceptShopSplits = (ready: PendingShopSplit[]) => {
    const payloads: Array<{
      item: ShopItem;
      parts: { token: string; field: string }[];
    }> = [];
    const needsAssign: PendingShopSplit[] = [];
    for (const row of ready) {
      const fields = readySplitFields(row.parts, splitHints, ignoredNames);
      if (!fields) {
        needsAssign.push(row);
        continue;
      }
      const parts = row.parts.map((token, index) => ({
        token,
        field: fields[index]!,
      }));
      if (
        !splitLands(row.item.occupied, row.item.field, parts, row.item.token)
      ) {
        needsAssign.push(row);
        continue;
      }
      payloads.push({ item: row.item, parts });
    }
    setMultiSplitRows([]);
    if (needsAssign.length > 0) {
      assignOpen.current = true;
      assignRows.current = needsAssign;
      setPendingShopSplits(needsAssign);
      setShopSplitGroups(
        needsAssign.map((row) => ({
          parent: row.item.display || row.item.token,
          parts: row.parts.map((token, index) => {
            const known =
              knownSplitField(token, splitHints, ignoredNames) || null;
            const taken = known
              ? blockedSplitField(
                  row.item.occupied,
                  row.item.field,
                  { token, field: known },
                  row.item.token
                )
              : null;
            return {
              token,
              id: splitPartId(row.item, index),
              nested: false,
              assignable: true,
              field: taken ? null : known,
              hint: taken
                ? occupiedFieldNote(taken, row.item.occupied?.[taken])
                : undefined,
            };
          }),
        }))
      );
      heldSplitPayloads.current = [];
      if (payloads.length) void applySplitBatch(payloads);
      return;
    }
    setPendingShopSplits(null);
    setShopSplitGroups(null);
    heldSplitPayloads.current = [];
    if (payloads.length > 0) void applySplitBatch(payloads);
    else closeMultiSplit();
  };

  const openSplitForSelection = () => {
    const rows = (
      selection.length > 0 ? selection : current ? [current] : []
    ).filter((item) => canSplitToken(item.display || item.token));
    if (rows.length > 1) {
      setSplitDraft(null);
      setSplitOpen(false);
      queuedShopSplits.current = [];
      setPendingShopSplits(null);
      setShopSplitGroups(null);
      setMultiSplitRows(rows);
      return;
    }
    setSplitDraft(null);
    setSplitOpen(true);
  };

  const resolveAlias = async () => {
    if (!current || !canonicalId || !selectedField) return;
    if (selection.some((item) => item.field !== current.field)) return;
    const batch = selection.length > 1 && selectedField === current.field;
    const rows = batch ? selection : [current];
    if (!batch) {
      const row = rows[0];
      if (!row) return;
      await finishResolve({
        token: row.token,
        field: selectedField,
        source_field: row.field,
        canonical_id: canonicalId,
        action: "alias",
      });
      return;
    }
    await runBatch(rows, (row) => ({
      token: row.token,
      field: row.field,
      source_field: row.field,
      canonical_id: canonicalId,
      action: "alias",
    }));
  };

  const createEntity = async () => {
    if (!current || !selectedField) return;
    if (
      selection.length > 1 &&
      (selectedField === "room" ||
        selectedField === "class_name" ||
        selectedField === "subject")
    ) {
      if (selectedField === "subject" && backgroundIds.size === 0) return;
      const backgrounds =
        selectedField === "subject" ? Array.from(backgroundIds) : undefined;
      await runBatch(selection, (row) => ({
        token: row.token,
        field: selectedField,
        source_field: row.field,
        action: "create",
        name: (row.display || row.token).trim(),
        ...(backgrounds ? { background_ids: backgrounds } : {}),
      }));
      return;
    }
    if (!newName.trim()) return;
    if (selectedField === "subject" && backgroundIds.size === 0) return;
    await finishResolve({
      token: current.token,
      field: selectedField,
      source_field: current.field,
      action: "create",
      name: newName.trim(),
      background_ids:
        selectedField === "subject" ? Array.from(backgroundIds) : undefined,
    });
  };

  const ignoreToken = async () => {
    if (!current) return;
    const rows = selection.length > 0 ? selection : [current];
    if (rows.length === 1) {
      const row = rows[0];
      if (!row) return;
      rememberIgnoredToken(row.token);
      setIgnoredNames((names) =>
        isIgnoredToken(row.token, names) ? names : [...names, row.token]
      );
      await finishResolve({
        token: row.token,
        field: row.field,
        source_field: row.field,
        action: "ignore",
      });
      return;
    }
    rememberIgnoredTokens(rows.map((row) => row.token));
    await runBatch(rows, (row) => ({
      token: row.token,
      field: row.field,
      source_field: row.field,
      action: "ignore",
    }));
  };

  const setPartField = (index: number, field: string) => {
    setSplitDraft((prev) => {
      if (!prev) return prev;
      const fields = [...prev.fields];
      if (field !== "ignore") {
        const other = fields.findIndex(
          (value, i) => i !== index && value === field
        );
        if (other >= 0) fields[other] = fields[index] || field;
      }
      fields[index] = field;
      return { ...prev, fields };
    });
  };

  const focusItem = (item: ShopItem) => {
    const index = items.findIndex(
      (row) => row.token === item.token && row.field === item.field
    );
    if (index >= 0) setItemIndex(index);
  };

  const toggleSelected = (item: ShopItem) => {
    const key = shopItemKey(item);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectedItems = (rows: ShopItem[]) => {
    const keys = rows.map(shopItemKey);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const allOn = keys.every((key) => next.has(key));
      for (const key of keys) {
        if (allOn) next.delete(key);
        else next.add(key);
      }
      return next;
    });
  };

  const goBack = () => {
    if (splitOpen) {
      setSplitOpen(false);
      return;
    }
    if (splitDraft) {
      setSplitDraft(null);
      return;
    }
    const pos = orderedItems.findIndex(
      (item) => item.token === current?.token && item.field === current?.field
    );
    const prev = orderedItems[pos - 1];
    if (!prev) return;
    focusItem(prev);
  };

  const undoLast = async () => {
    if (!canUndo || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await bulkTimetableImportApi.undoShopItem(batchId);
      if (result.ignored_tokens) {
        rememberIgnoredTokens(result.ignored_tokens);
        setIgnoredNames(result.ignored_tokens);
      }
      applyRemaining(
        result.remaining || [],
        result.targets,
        false,
        result.item_index || 0,
        !!result.can_undo,
        result.ignored_tokens || []
      );
    } catch (e) {
      const ax = e as {
        response?: { data?: { detail?: string } };
        message?: string;
      };
      setError(
        ax.response?.data?.detail || ax.message || "Undo fehlgeschlagen"
      );
    } finally {
      setBusy(false);
    }
  };

  const applySplit = async () => {
    if (!current || !splitDraft) return;
    const parts = splitDraft.parts
      .map((token, index) => ({ token, field: splitDraft.fields[index] || "" }))
      .filter((part) => part.token && part.field);
    if (parts.length < 2) return;
    await finishResolve({
      token: current.token,
      field: current.field,
      source_field: current.field,
      action: "split",
      parts,
    });
  };

  if (dismissed) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5 space-y-4">
          <h2 className="text-lg font-semibold">Bulk-Zuordnung</h2>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Wird geladen…
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 text-sm border rounded"
          >
            Abbrechen
          </button>
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5 space-y-4">
          <h2 className="text-lg font-semibold">Bulk-Zuordnung</h2>
          <p className="text-sm text-red-600">
            {error || "Keine offenen Tokens."}
          </p>
          <div className="flex gap-2">
            {canUndo && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void undoLast()}
                className="px-3 py-2 text-sm bg-amber-500 text-white rounded disabled:opacity-50"
              >
                Undo
              </button>
            )}
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-2 text-sm border rounded"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    );
  }

  const showPreview = previewOpen && !!fileId && pages.length > 0;
  const previewPage = pages[previewIdx] ?? pages[0];
  const pageTeacher = previewPage ? pageTeachers[previewPage] : undefined;
  const progressed = Math.max(resolvedPages, previewPage ? previewPage - 1 : 0);
  const pageProgress =
    totalPages > 0
      ? Math.round((Math.min(progressed, totalPages) / totalPages) * 100)
      : 0;
  const fieldLabel = FIELD_LABELS[selectedField] || selectedField;
  const createLabel = CREATE_LABELS[selectedField] || "Neu anlegen";
  const mixedSelection = selection.some((item) => item.field !== current.field);
  const categoryChanged = selectedField !== current.field;
  const thematicPos = orderedItems.findIndex(
    (item) => item.token === current.token && item.field === current.field
  );
  const aliasCount =
    !mixedSelection && !categoryChanged && selection.length > 1
      ? selection.length
      : 0;
  const batchCreate =
    selection.length > 1 && Boolean(BATCH_CREATE_BUTTON[selectedField]);
  const batchCreateLabel =
    BATCH_CREATE_BUTTON[selectedField]?.(selection.length) ||
    `${selection.length} Einträge anlegen`;
  const batchCreateNote =
    BATCH_CREATE_NOTE[selectedField] ||
    "Jeder Haken wird ein eigener Eintrag. Der Name ist das Token.";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div
        className={`bg-white rounded-lg shadow-xl w-full p-5 flex flex-col max-h-[90vh] overflow-hidden ${showPreview ? "max-w-7xl" : "max-w-6xl"}`}
      >
        <div className="flex items-start justify-between gap-3 shrink-0">
          <h2 className="text-lg font-semibold">Bulk-Zuordnung</h2>
          <div className="inline-flex overflow-hidden rounded border text-sm">
            <button
              type="button"
              className={`px-3 py-1 ${listMode === "single" ? "bg-gray-900 text-white" : "bg-white text-gray-600"}`}
              onClick={() => {
                setListMode("single");
                setSelectedKeys(new Set());
              }}
            >
              Einzeln
            </button>
            <button
              type="button"
              className={`px-3 py-1 ${listMode === "multi" ? "bg-gray-900 text-white" : "bg-white text-gray-600"}`}
              onClick={() => setListMode("multi")}
            >
              Mehrere
            </button>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm shrink-0">
          <span className="font-medium text-gray-800">
            {previewPage
              ? `Seite ${previewPage}${totalPages ? ` von ${totalPages}` : ""}`
              : `${Math.max(thematicPos, 0) + 1}/${Math.max(items.length, 1)} offen`}
          </span>
          {totalPages > 1 && (
            <span className="flex items-center gap-2 text-xs text-gray-500">
              <span className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-200">
                <span
                  className="block h-full bg-blue-600"
                  style={{ width: `${pageProgress}%` }}
                />
              </span>
              {Math.min(progressed, totalPages)}/{totalPages}
            </span>
          )}
          {pageTeacher && (
            <span className="text-gray-700">Lehrer: {pageTeacher}</span>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-2 shrink-0">
          Jede unbekannte Zeichenkette einmal. Bulk ist nicht fertig, solange
          Zeilen offen sind. Undo legt den Token wieder an denselben Shop-Ort.
        </p>
        <div className="min-h-0 flex-1 mt-4 flex gap-4">
          <div className="w-72 shrink-0 min-h-0 overflow-y-auto border rounded">
            <div className="sticky top-0 z-10 border-b bg-white px-3 py-2 text-xs text-gray-500">
              {items.length} offen ·{" "}
              {listMode === "single"
                ? "Zeile klicken, kein Haken"
                : "Haken an der Rubrik wählt alle, Zeile bleibt einzeln"}
            </div>
            {shopGroups.map((group) => {
              const groupItems = group.themes.flatMap((theme) => theme.items);
              const count = groupItems.length;
              const groupKeys = groupItems.map(shopItemKey);
              const groupOn = groupKeys.filter((key) =>
                selectedKeys.has(key)
              ).length;
              return (
                <div key={group.field}>
                  <div className="border-b bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-600 flex items-center gap-2">
                    {listMode === "multi" && (
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={groupOn === count && count > 0}
                        ref={(node) => {
                          if (node)
                            node.indeterminate = groupOn > 0 && groupOn < count;
                        }}
                        aria-label={`${group.label} auswählen`}
                        onChange={() => toggleSelectedItems(groupItems)}
                      />
                    )}
                    <span>
                      {group.label} · {count}
                    </span>
                  </div>
                  {group.themes.map((theme) => {
                    const themeKeys = theme.items.map(shopItemKey);
                    const themeOn = themeKeys.filter((key) =>
                      selectedKeys.has(key)
                    ).length;
                    return (
                      <div key={`${group.field}-${theme.label}`}>
                        <div className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-2">
                          {listMode === "multi" && (
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5"
                              checked={
                                themeOn === theme.items.length &&
                                theme.items.length > 0
                              }
                              ref={(node) => {
                                if (node)
                                  node.indeterminate =
                                    themeOn > 0 && themeOn < theme.items.length;
                              }}
                              aria-label={`${group.label}: ${theme.label} auswählen`}
                              onChange={() => toggleSelectedItems(theme.items)}
                            />
                          )}
                          <span>{theme.label}</span>
                        </div>
                        {theme.items.map((item) => {
                          const key = shopItemKey(item);
                          const isCurrent =
                            item.token === current.token &&
                            item.field === current.field;
                          return (
                            <div
                              key={key}
                              className={`flex items-center gap-1 pr-2 ${isCurrent ? "bg-blue-50" : ""}`}
                            >
                              {listMode === "multi" && (
                                <input
                                  type="checkbox"
                                  className="ml-2 h-4 w-4"
                                  checked={selectedKeys.has(key)}
                                  aria-label={`${item.display || item.token} auswählen`}
                                  onChange={() => toggleSelected(item)}
                                />
                              )}
                              <button
                                type="button"
                                data-testid="shop-token"
                                data-token={item.token}
                                data-field={item.field}
                                data-theme={theme.label}
                                aria-current={isCurrent ? "true" : undefined}
                                onClick={() => focusItem(item)}
                                className={`min-w-0 flex-1 px-3 py-2 text-left text-sm ${isCurrent ? "border-l-2 border-blue-600 font-medium" : "border-l-2 border-transparent"}`}
                              >
                                <span className="font-mono">
                                  {item.display || item.token}
                                </span>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          <div
            className={`${showPreview ? "w-80 shrink-0" : "flex-1"} min-h-0 overflow-y-auto space-y-4`}
          >
            <div className="text-sm flex items-center flex-wrap gap-2">
              <span className="font-medium">{current.display}</span>
              <span className="text-gray-400">
                {items.length > 1
                  ? `${thematicPos + 1}/${items.length} offen`
                  : `${items.length} offen`}
              </span>
              {fileId && pages.length > 0 && (
                <button
                  type="button"
                  onClick={() => setPreviewOpen((open) => !open)}
                  className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-0.5 bg-blue-50 px-1.5 py-0.5 rounded"
                >
                  <Eye className="w-3 h-3" />
                  {previewOpen
                    ? "PDF schliessen"
                    : pages.length === 1
                      ? "1 PDF-Seite"
                      : `${pages.length} PDF-Seiten`}
                </button>
              )}
            </div>

            {!splitDraft && (
              <div className="space-y-1">
                <p className="text-xs text-gray-500">
                  Kategorie{" "}
                  {selectedField !== current.field
                    ? "geändert"
                    : "ändern, falls falsch erkannt"}
                </p>
                <div className="flex gap-1">
                  {SHOP_FIELDS.map((field) => (
                    <button
                      key={field}
                      type="button"
                      disabled={busy}
                      onClick={() => setFieldOverride(field)}
                      className={`px-2 py-1 text-xs rounded border ${
                        selectedField === field
                          ? "bg-blue-600 text-white border-blue-600"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"
                      } disabled:opacity-50`}
                    >
                      {FIELD_LABELS[field]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {splitDraft ? (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">
                  Jedes Teil einer Kategorie zuordnen, danach Teile übernehmen.
                  Gleiche Kategorie nur einmal, Ignorieren beliebig oft.
                </p>
                {splitDraft.parts.map((part, index) => {
                  const known = uniqueCatalogHit(targets, part);
                  const ignored =
                    splitDraft.fields[index] === "ignore" &&
                    isIgnoredToken(part, ignoredNames);
                  const locked =
                    ignored ||
                    Boolean(known && splitDraft.fields[index] === known.field);
                  return (
                    <div key={`${part}-${index}`} className="space-y-1">
                      <code className="text-sm font-mono">{part}</code>
                      {locked && ignored ? (
                        <p className="text-xs text-gray-500">schon ignoriert</p>
                      ) : locked && known ? (
                        <p className="text-xs text-emerald-700">
                          schon {known.name}
                        </p>
                      ) : (
                        <div className="flex gap-1">
                          {PART_FIELDS.map((field) => (
                            <button
                              key={field}
                              type="button"
                              disabled={busy}
                              onClick={() => setPartField(index, field)}
                              className={`px-2 py-1 text-xs rounded border ${
                                splitDraft.fields[index] === field
                                  ? "bg-blue-600 text-white border-blue-600"
                                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
                              } disabled:opacity-50`}
                            >
                              {FIELD_LABELS[field]}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : selectedField === "subject" ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <div
                  className={`rounded-lg border p-3 ${mode === "alias" ? "border-blue-600 bg-blue-50/50 ring-2 ring-blue-200" : "border-gray-200"}`}
                  onClick={() => setMode("alias")}
                >
                  <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    Alias zuordnen
                  </p>
                  <p className="mt-1 text-xs text-emerald-700">
                    Matrix unverändert.
                  </p>
                  {identityMatch && (
                    <p className="text-sm text-gray-600 mt-2">
                      «{current.display}» ist schon der {fieldLabel}-Name.
                      Übernehmen, kein Alias.
                    </p>
                  )}
                  <input
                    className="w-full border rounded px-2 py-2 text-sm mt-2"
                    value={search}
                    onChange={(e) => {
                      setMode("alias");
                      setSearch(e.target.value);
                    }}
                    onFocus={() => setMode("alias")}
                    placeholder={`${fieldLabel} suchen (Name, Code, Alias)`}
                  />
                  <div className="max-h-40 overflow-y-auto border rounded mt-2 bg-white">
                    {filtered.length === 0 ? (
                      <div className="p-3 text-sm text-gray-500 space-y-2">
                        <p>Kein passendes {fieldLabel} in der Liste.</p>
                        <button
                          type="button"
                          className="text-sm text-blue-600 hover:underline"
                          onClick={() => setMode("create")}
                        >
                          {createLabel} anlegen
                        </button>
                      </div>
                    ) : (
                      filtered.map((target) => (
                        <button
                          key={target.id}
                          type="button"
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                            canonicalId === target.id
                              ? "bg-blue-50 border-l-2 border-blue-500"
                              : ""
                          }`}
                          onClick={() => {
                            setMode("alias");
                            setCanonicalId(target.id);
                          }}
                        >
                          <span className="font-medium">{target.name}</span>
                          {target.code && (
                            <span className="ml-2 text-xs text-gray-500">
                              {target.code}
                            </span>
                          )}
                          {!!target.aliases?.length && (
                            <span className="block text-xs text-gray-400 truncate">
                              Alias: {target.aliases.join(", ")}
                            </span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
                <div
                  className={`rounded-lg border p-3 ${mode === "create" ? "border-blue-600 bg-blue-50/50 ring-2 ring-blue-200" : "border-gray-200"}`}
                  onClick={() => setMode("create")}
                >
                  <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    Neues Fach
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    {batchCreate
                      ? `${batchCreateNote} Pflicht: mindestens ein Hintergrund, gilt für alle.`
                      : "Neues Fach. Pflicht: mindestens ein Hintergrund."}
                  </p>
                  {!batchCreate && (
                    <input
                      className="w-full border rounded px-2 py-2 text-sm mt-2"
                      value={newName}
                      onChange={(e) => {
                        setMode("create");
                        setNewName(e.target.value);
                      }}
                      onFocus={() => setMode("create")}
                      placeholder={`${fieldLabel}-Name`}
                    />
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {backgrounds.map((bg) => {
                      const on = backgroundIds.has(bg.id);
                      return (
                        <button
                          key={bg.id}
                          type="button"
                          className={`rounded-full border px-2.5 py-1 text-xs ${
                            on
                              ? "border-emerald-400 bg-emerald-50 text-emerald-800 font-semibold"
                              : "border-gray-300 bg-white"
                          }`}
                          onClick={() => {
                            setMode("create");
                            setBackgroundIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(bg.id)) next.delete(bg.id);
                              else next.add(bg.id);
                              return next;
                            });
                          }}
                        >
                          {bg.code || bg.name}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 rounded border border-dashed border-gray-300 bg-white px-2 py-2 text-xs text-gray-600">
                    {batchCreate ? (
                      "Jedes Token eine eigene Spalte"
                    ) : (
                      <>
                        Neue Spalte{" "}
                        <strong>{newName.trim() || current.display}</strong>
                      </>
                    )}
                    <br />
                    Zelle:{" "}
                    {backgroundIds.size
                      ? `Qualifiziert · ${backgrounds
                          .filter((b) => backgroundIds.has(b.id))
                          .map((b) => b.name)
                          .join(", ")}`
                      : "—"}
                  </div>
                  {mode === "create" && backgroundIds.size === 0 && (
                    <p className="mt-2 text-xs text-amber-800">
                      Ohne Hintergrund bleibt Speichern gesperrt.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="flex border-b">
                  <button
                    type="button"
                    className={`px-3 py-2 text-sm font-medium border-b-2 ${
                      mode === "alias"
                        ? "border-blue-600 text-blue-600"
                        : "border-transparent text-gray-500"
                    }`}
                    onClick={() => setMode("alias")}
                  >
                    Alias zuordnen
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-2 text-sm font-medium border-b-2 ${
                      mode === "create"
                        ? "border-blue-600 text-blue-600"
                        : "border-transparent text-gray-500"
                    }`}
                    onClick={() => setMode("create")}
                  >
                    {createLabel}
                  </button>
                </div>

                {mode === "alias" && (
                  <div className="space-y-2">
                    {identityMatch && (
                      <p className="text-sm text-gray-600">
                        «{current.display}» ist schon der {fieldLabel}-Name.
                        Übernehmen, kein Alias.
                      </p>
                    )}
                    <input
                      className="w-full border rounded px-2 py-2 text-sm"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={`${fieldLabel} suchen (Name, Code, Alias)`}
                    />
                    <div className="max-h-48 overflow-y-auto border rounded">
                      {filtered.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 space-y-2">
                          <p>Kein passendes {fieldLabel} in der Liste.</p>
                          <button
                            type="button"
                            className="text-sm text-blue-600 hover:underline"
                            onClick={() => setMode("create")}
                          >
                            {createLabel} anlegen
                          </button>
                        </div>
                      ) : (
                        filtered.map((target) => (
                          <button
                            key={target.id}
                            type="button"
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                              canonicalId === target.id
                                ? "bg-blue-50 border-l-2 border-blue-500"
                                : ""
                            }`}
                            onClick={() => setCanonicalId(target.id)}
                          >
                            <span className="font-medium">{target.name}</span>
                            {target.code && (
                              <span className="ml-2 text-xs text-gray-500">
                                {target.code}
                              </span>
                            )}
                            {!!target.aliases?.length && (
                              <span className="block text-xs text-gray-400 truncate">
                                Alias: {target.aliases.join(", ")}
                              </span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {mode === "create" && (
                  <div className="space-y-2">
                    {batchCreate ? (
                      <p className="text-sm text-gray-600">{batchCreateNote}</p>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600">
                          {fieldLabel} existiert nicht? Als neuen Katalogeintrag
                          anlegen. «{current.display}» wird als Alias
                          gespeichert, falls der Name abweicht.
                        </p>
                        <input
                          className="w-full border rounded px-2 py-2 text-sm"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          placeholder={`${fieldLabel}-Name`}
                        />
                      </>
                    )}
                  </div>
                )}
              </>
            )}

            {mixedSelection && (
              <p className="text-xs text-amber-800">
                Auswahl mischt Kategorien. Alias gilt nicht für die Haken.
              </p>
            )}
            {selection.length > 1 && (
              <p className="text-xs text-amber-800">
                {mode === "create" && batchCreate
                  ? "Legt für jeden Haken einen eigenen Eintrag an. Der Name ist das Token."
                  : categoryChanged
                    ? "Kategorie geändert gilt nur für die offene Zeile."
                    : "Neu gilt nur für die offene Zeile. Teilen gilt für die Haken."}
              </p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          {showPreview && (
            <div className="flex-1 min-h-0 border rounded overflow-hidden">
              <PdfPanel
                fileId={fileId}
                currentPage={previewPage ?? 1}
                allowedPages={pages}
                onPageChange={(page) => {
                  const index = pages.indexOf(page);
                  if (index >= 0) setPreviewIdx(index);
                }}
              />
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 pt-4 mt-4 border-t shrink-0">
          <button
            type="button"
            disabled={busy || (!splitDraft && !splitOpen && thematicPos <= 0)}
            onClick={goBack}
            className="h-10 w-full px-2 text-sm border rounded text-gray-700 disabled:opacity-50"
          >
            Zurück
          </button>
          <button
            type="button"
            disabled={busy || !canUndo}
            onClick={() => void undoLast()}
            title={
              canUndo
                ? "Letzte Zuordnung rückgängig"
                : "Keine Aktion zum Rückgängigmachen"
            }
            className="h-10 w-full px-2 text-sm bg-amber-500 text-white rounded disabled:opacity-50 disabled:bg-gray-100 disabled:text-gray-400"
          >
            Undo
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="h-10 w-full px-2 text-sm border rounded"
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={ignoreToken}
            title="Wird im Template gespeichert. Undo legt den Token wieder in den Shop."
            className="h-10 w-full px-2 text-sm border rounded text-gray-700 disabled:opacity-50"
          >
            Ignorieren
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={openSplitForSelection}
            title={
              selection.length > 1
                ? `${selection.length} ausgewählte Token teilen`
                : "Offene Zeile teilen"
            }
            className="h-10 w-full px-2 text-sm border rounded text-gray-700 disabled:opacity-50"
          >
            Teilen
          </button>
          {splitDraft ? (
            <button
              type="button"
              disabled={
                busy ||
                splitDraft.parts.length < 2 ||
                splitDraft.fields.some((field) => !field) ||
                new Set(splitDraft.fields.filter((field) => field !== "ignore"))
                  .size !==
                  splitDraft.fields.filter((field) => field !== "ignore").length
              }
              onClick={applySplit}
              title={
                splitDraft.fields.some((field) => !field)
                  ? "Jedes Teil einer Kategorie zuordnen"
                  : "Teile übernehmen"
              }
              className="h-10 w-full px-2 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
            >
              Teile übernehmen
            </button>
          ) : mode === "alias" ? (
            <button
              type="button"
              disabled={busy || !canonicalId || mixedSelection}
              onClick={resolveAlias}
              className="h-10 w-full px-2 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
            >
              {aliasCount > 1
                ? `Als Alias auf ${aliasCount} Tokens`
                : identityMatch
                  ? `${fieldLabel} übernehmen`
                  : "Als Alias zuordnen"}
            </button>
          ) : (
            <button
              type="button"
              disabled={
                busy ||
                (!batchCreate && !newName.trim()) ||
                (selectedField === "subject" && backgroundIds.size === 0)
              }
              onClick={createEntity}
              className="h-10 w-full px-2 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
            >
              {batchCreate ? batchCreateLabel : `${createLabel} anlegen`}
            </button>
          )}
        </div>
        <SplitDialog
          isOpen={splitOpen}
          token={current.display || current.token}
          selectedTokens={[current.display || current.token]}
          onApply={(parts) => {
            const valid = parts.filter((part) => part.trim());
            if (valid.length < 2 || !current) {
              setSplitOpen(false);
              return;
            }
            void (async () => {
              const names = await loadIgnoredTokenNames().catch(() =>
                ignoredNames.length ? ignoredNames : knownIgnoredTokenNames()
              );
              setIgnoredNames(names);
              const fields = guessPartFields(
                valid,
                current.field,
                targets,
                names
              );
              const allKnown = valid.every((part, index) => {
                if (fields[index] === "ignore") return true;
                const hit = uniqueCatalogHit(targets, part);
                return Boolean(hit && hit.field === fields[index]);
              });
              setSplitOpen(false);
              if (allKnown) {
                void finishResolve({
                  token: current.token,
                  field: current.field,
                  source_field: current.field,
                  action: "split",
                  parts: valid.map((token, index) => ({
                    token,
                    field: fields[index] ?? "",
                  })),
                });
                return;
              }
              setSplitDraft({ parts: valid, fields });
            })();
          }}
          onCancel={() => setSplitOpen(false)}
        />
        {multiSplitRows.length > 0 ? (
          <MultiSplitDialog
            isOpen
            tokens={multiSplitRows.map((row) => row.display || row.token)}
            hints={splitHints}
            onApply={(results, options) => {
              const pending = results
                .filter((row) => row.parts.length >= 2)
                .flatMap((row) =>
                  shopRowsForDialogToken(multiSplitRows, row.token).map(
                    (item) => ({ item, parts: row.parts })
                  )
                );
              const revokeIds = (options?.revoke || []).flatMap((token) =>
                shopRowsForDialogToken(multiSplitRows, token).map(shopItemKey)
              );
              const ready = mergeSplitQueue(
                queuedShopSplits.current,
                pending,
                revokeIds,
                (row) => [shopItemKey(row.item)]
              );
              if (options?.close === false) {
                queuedShopSplits.current = ready;
                return;
              }
              queuedShopSplits.current = [];
              if (ready.length === 0) {
                closeMultiSplit();
                return;
              }
              acceptShopSplits(ready);
            }}
            onCancel={closeMultiSplit}
          />
        ) : null}
        {pendingShopSplits && shopSplitGroups ? (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
            <div className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl">
              <div className="border-b border-gray-200 px-3 py-2 text-sm font-semibold">
                Teile zuordnen ·{" "}
                {pendingShopSplits.length === 1
                  ? pendingShopSplits[0]!.item.display ||
                    pendingShopSplits[0]!.item.token
                  : `${pendingShopSplits.length} Token`}
              </div>
              <SplitPartAssignPanel
                groups={shopSplitGroups}
                requireComplete
                onApply={(assignments) => {
                  const fieldOf = new Map(
                    assignments
                      .filter((row) => row.id)
                      .map((row) => [row.id!, row.field])
                  );
                  const payloads = [];
                  for (const row of pendingShopSplits) {
                    const parts = row.parts.map((token, index) => {
                      const field = fieldOf.get(splitPartId(row.item, index));
                      return field ? { token, field } : null;
                    });
                    if (parts.some((part) => !part) || parts.length < 2) {
                      setError("Jedes Teil einer Kategorie zuordnen");
                      return;
                    }
                    payloads.push({
                      item: row.item,
                      parts: parts.flatMap((part) => (part ? [part] : [])),
                    });
                  }
                  const ready = heldSplitPayloads.current;
                  heldSplitPayloads.current = [];
                  assignOpen.current = false;
                  assignRows.current = [];
                  setPendingShopSplits(null);
                  setShopSplitGroups(null);
                  void applySplitBatch([...ready, ...payloads]);
                }}
                onCancel={closeMultiSplit}
              />
            </div>
          </div>
        ) : null}
        {batchProgress && (
          <ProgressDialog
            title={batchProgress.title}
            current={batchProgress.current}
            total={batchProgress.total}
            status={batchProgress.status}
            hint={BATCH_PROGRESS_HINT}
            onCancel={() => batchAbort.current?.abort()}
            cancelLabel="Stoppen"
          />
        )}
      </div>
    </div>
  );
}
